import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, ReplaySubject, Subject } from 'rxjs';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { map, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SbEvent, SbEventBusService, SbEventTopic } from '@services';
import { TempViewServiceService } from '../service/temp-view-service.service';
import { AcctsService } from '../../../shared/state/accts/accts.service';

@Component({
    selector: 'app-sb-filter',
    template: `
        <form [attr.hidden]="$hidden | async" [formGroup]="viewForm">
            <ng-container appPopoverContainer [control]="viewForm.controls.selection">
                <button type="button" class="filter-item" appPopoverTrigger>
                    <span class="label">View</span>
                    {{ $selection | async }}
                </button>
                <div class="popover" appPopover [config]="{ multiselect: true }">
                    <ul role="listbox" class="dropdown-list" aria-multiselectable="true">
                        <li
                            role="option"
                            *ngFor="let option of $options | async; trackBy: trackByFn"
                            [appPopoverOption]="option"
                        >
                            <button type="button">
                                <span class="check"></span>
                                {{ option.label }}
                            </button>
                        </li>
                    </ul>
                </div>
            </ng-container>
        </form>
    `,
    styleUrls: ['./sb-filter.component.scss']
})
export class SbFilterComponent implements OnInit, OnDestroy {
    unsubscribe$ = new Subject();
    constructor(
        private _fb: UntypedFormBuilder,
        private eventBus: SbEventBusService,
        private viewService: TempViewServiceService,
        private _acctsService: AcctsService
    ) {}

    get viewOptions() {
        return this.viewForm.controls.selection as UntypedFormArray;
    }
    viewForm = this._fb.group({
        selection: new UntypedFormArray([])
    });

    $options = this.eventBus.getObservable(SbEventTopic.ReloadAccounts).pipe(
        startWith(true),
        switchMap((_) => this.viewService.getViews()),
        map((options) => options.map((option) => ({ ...option, selected: true }))),
        tap((options) => {
            this.addOptions(options);
            this.$selection.next(
                this.getSelection(this.viewForm.controls.selection.value, options)
            );
        }),
        shareReplay(1)
    );

    $selection = new ReplaySubject(1);

    $hidden = this.$options.pipe(
        // Either render hidden attribute - or don't by setting null
        map((options) => (options.length === 1 ? true : null))
    );

    private addOptions(options) {
        // get existing options and their values
        const existingOptions = this.viewOptions.controls.map(
            (control) => control.value.value
        );
        // for each option...
        options.forEach((option, index) => {
            // ...if it doesn't exist yet, add it
            if (existingOptions.indexOf(option.value) < 0) {
                this.viewOptions.push(new UntypedFormControl(option), {
                    emitEvent: options.length === index + 1
                });
            }
        });
    }

    ngOnInit(): void {
        combineLatest([this.viewForm.controls.selection?.valueChanges, this.$options])
            .pipe(
                tap(([selection, options]) => {
                    this.$selection.next(this.getSelection(selection, options));
                }),
                tap(([selection, options]) => {
                    // get active account ids from the selected options
                    const accountIds = selection.reduce((acc, option) => {
                        if (option.selected) {
                            acc.push(option.value);
                        }
                        return acc;
                    }, []);
                    this._acctsService.setActiveAccount(accountIds);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(([selection, options]) => {
                const payload: SbEvent = {
                    source: 'account-selection-multi-select',
                    topic: SbEventTopic.AccountSelected,
                    payload: {
                        itemValue: null,
                        value: selection
                            .filter((option) => option.selected === true)
                            .map((option) => option.value)
                    }
                };
                try {
                    this.eventBus.emit(payload);
                } catch (e) {
                    console.error(e);
                }
            });
    }

    ngOnDestroy() {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    getSelection(selection, options) {
        const selected = selection.filter((option) => option.selected);
        if (selected.length === options.length) {
            return 'All';
        } else if (selected.length > 1) {
            return `${selected.length} of ${options.length}`;
        } else if (selected.length === 1) {
            return selected[0].label;
        } else {
            return 'Nothing';
        }
    }

    trackByFn(index, item) {
        return item.value;
    }
}
